import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import md5 from 'md5';
import { useAuthStorage } from '@/modules/f8/hooks';
const authStorage = useAuthStorage();

const auth = {
    accessToken: null, // Токен доступа
    refreshToken: null, // Токен обновления
    currentUser: null, // Текущий пользователь
    userData: {}, // Данные пользователя
    baseUrl: `${process.env.VUE_APP_URL_GATEWAY}/v1/auth`,
    loginUrl: `${process.env.VUE_APP_URL_GATEWAY}/v1/auth/init`,
    refreshTokenUrl: `${process.env.VUE_APP_URL_GATEWAY}/v1/auth/refresh`,
    logoutTokenUrl: `${process.env.VUE_APP_URL_GATEWAY}/v1/auth/logout`,
    inlineLogInTimeout: 10000,

    // Получение экземпляра axios с заголовком авторизации
    getAxiosInstance: (baseUrl) => {
        const axiosInstance = axios.create({
            baseURL: baseUrl,
            timeout: 30000,
        });

        // Добавляем заголовок авторизации для всех запросов
        axiosInstance.interceptors.request.use((config) => {
            config.headers['Authorization'] = `Bearer ${auth.getAccessToken()}`;
            return config;
        });

        // Добавляем перехватчик ошибок для всех запросов
        axiosInstance.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                // Если запрос не получил ответа, возвращаем ошибку как есть
                if (!error.response || !error.response.status) {
                    return Promise.reject(error);
                }

                // Проверяем, что ошибка 401 Unauthorized и это не повторная попытка
                if (error.response.status === 401 && !originalRequest._retry) {
                    // Если это первая попытка повторить запрос
                    originalRequest._retry = true;

                    try {
                        // Пытаемся обновить токен
                        await auth.refreshAccessToken();
                    } catch (e) {
                        // Если обновление токена не удалось, выходим 
                        const newError = new Error('Unauthorized');
                        return Promise.reject(newError);
                    }

                    // После обновления токена повторяем оригинальный запрос с новым токеном
                    originalRequest.headers['Authorization'] = `Bearer ${auth.getAccessToken()}`;
                    return axiosInstance(originalRequest);
                }

                // Если ошибка не 401 или повторная попытка, возвращаем ошибку как есть
                return Promise.reject(error);
            }
        );

        return axiosInstance;
    },

    login: async (p) => {
        auth.clearAll();

        const req = {
            "email": p.email,
            "password": md5(p.password)
        }

        try {
            const response = await axios.post(auth.loginUrl, req, { timeout: 10000 });

            if (response.data && response.data.data) {
                const { access_token, refresh_token } = response.data.data;
                auth.setAccessToken(access_token);
                auth.setRefreshToken(refresh_token);
                return true;
            }
            throw new Error("Login failed: No data received");
        } catch (error) {
            if (error.response && error.response.data.error) {
                const err = error.response.data.error
                if (err.code == "PHC010") {
                    throw new Error("Login failed: Identity service error");
                }

                throw new Error(err.message);
            }

            console.error('LogIn error:', error);
            throw new Error("Login failed");
        }
    },

    refreshAccessToken: async () => {
        if (!auth.getRefreshToken()) {
            auth.clearAll();
            return false;
        }

        try {
            const response = await axios.post(auth.refreshTokenUrl, {
                "refresh_token": auth.getRefreshToken(),
            }, {
                timeout: 10000
            });

            if (response.data && response.data.data.access_token) {
                auth.setAccessToken(response.data.data.access_token);
            }

        } catch (error) {
            if (error.response && error.response.data.error) {
                const res = auth.requestInlineLogin(auth.inlineLogInTimeout, { source: 'auth' })
                    .then(async (data) => {
                        const loginRes = await auth.login({
                            email: auth.userData.email,
                            password: data.password
                        });
                        return loginRes;
                    })
                    .catch(() => {
                        document.dispatchEvent(new CustomEvent('inlineLogInTimeout'));
                        return false;
                    });

                if (res) {
                    return res;
                }

                console.error('refresh token error: ' + error.response.data.error);
                return false;
            }
            console.error('refresh error: ' + error.message);
            return false;
        }
        return true;
    },

    requestInlineLogin: function (timeoutMs = 5000, initPayload = {}) {
        return new Promise((resolve, reject) => {
            const onSubmit = (e) => {
                window.removeEventListener('inlineLogInSubmit', onSubmit);
                clearTimeout(timer);
                resolve(e.detail);
            };

            const timer = setTimeout(() => {
                window.removeEventListener('inlineLogInSubmit', onSubmit);
                reject(new Error('inlineLogInSubmit not received within timeout'));
            }, timeoutMs);

            window.addEventListener('inlineLogInSubmit', onSubmit);

            // Выпустить init (можно использовать document или window — в вашем компоненте используется document)
            const initEvent = new CustomEvent('inlineLogInInit', { detail: initPayload });
            document.dispatchEvent(initEvent);
        });
    },

    logout: async () => {
        try {
            await auth.getAxiosInstance(auth.logoutTokenUrl).post(null, {
                "refresh_token": auth.getRefreshToken(),
            }, {
                timeout: 10000
            });
        } catch (error) {
            console.error('LogOut error:', error);
        }
        auth.clearAll();
    },

    ////////////////////////////////////////////////////////////////////////////

    getCurrentUserData: () => {
        const user = auth.getCurrentUser();
        if (user) {
            return auth.userData;
        }

        return null;
    },

    getCurrentUser: () => {
        if (auth.currentUser) {
            return auth.currentUser;
        }

        const user = authStorage.get('currentUser');
        if (user) {
            auth.currentUser = JSON.parse(user);
            auth.userData = auth.currentUser.payload.user;
            // if exipred
            if (auth.currentUser.exp < Date.now() / 1000) {
                auth.clearAll();
                return null;
            }

            return auth.currentUser;
        }

        return null;
    },

    getAccessToken: () => {
        if (auth.accessToken) {
            return auth.accessToken;
        }

        const token = authStorage.get('accessToken');
        if (token) {
            auth.accessToken = token;
            return auth.accessToken;
        }

        return '';
    },

    getRefreshToken: () => {
        if (auth.refreshToken) {
            return auth.refreshToken;
        }

        const token = authStorage.get('refreshToken');
        if (token) {
            auth.refreshToken = token;
            return auth.refreshToken;
        }

        return '';
    },

    setAccessToken: (token) => {
        auth.accessToken = token;
        authStorage.set('accessToken', token);
        auth.setCurrentUser(jwtDecode(token));
    },

    setRefreshToken: (token) => {
        auth.refreshToken = token;
        authStorage.set('refreshToken', token);
    },

    setCurrentUser: (userData) => {
        auth.currentUser = userData;
        authStorage.set('currentUser', JSON.stringify(userData));
    },

    clearAll: () => {
        auth.accessToken = '';
        auth.refreshToken = '';
        auth.currentUser = null;

        authStorage.clear();
    },
};

export function useAuth() {
    return {
        login: auth.login,
        logout: auth.logout,
        // getCurrentUser: auth.getCurrentUser,
        getCurrentUserData: auth.getCurrentUserData,
        refreshAccessToken: auth.refreshAccessToken,
        clearAll: auth.clearAll,
        getAxiosInstance: auth.getAxiosInstance,
    }
}